import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const fileUrl = (id) => (id ? `${API}/files/${id}` : null);

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("acteio_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const company = localStorage.getItem("acteio_company");
  if (company) config.headers["X-Company"] = company;
  return config;
});

export function applyAccent(hsl) {
  const root = document.documentElement;
  if (!hsl) {
    // Pas de société active — retirer l'override pour laisser le CSS brand Acteio s'appliquer
    root.style.removeProperty("--primary");
    root.style.removeProperty("--ring");
    return;
  }
  root.style.setProperty("--primary", hsl);
  root.style.setProperty("--ring", hsl);
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/** Extrait le message lisible d'une erreur axios (NestJS + fallback) */
export function formatAxiosError(err) {
  if (!err.response) return "Impossible de contacter le serveur. Vérifiez votre connexion.";
  const d = err.response.data;
  // NestJS: { message, statusCode } — message peut être une string OU un tableau
  // (erreurs de validation class-validator : un message par champ invalide).
  if (d?.message && typeof d.message === "string") return d.message;
  if (Array.isArray(d?.message) && d.message.length) return d.message.join(" ");
  // FastAPI: { detail }
  const fromDetail = formatApiErrorDetail(d?.detail);
  if (fromDetail) return fromDetail;
  return `Erreur ${err.response.status}`;
}

const SAFE_REDIRECT_PREFIXES = [
  'https://checkout.stripe.com/',
  'https://billing.stripe.com/',
  'https://www.paypal.com/',
  'https://www.sandbox.paypal.com/',
];

export function safeRedirect(url) {
  if (!url || typeof url !== 'string') return;
  if (!SAFE_REDIRECT_PREFIXES.some((p) => url.startsWith(p))) {
    console.error('[SECURITY] Redirection bloquée vers une URL non whitelistée :', url);
    return;
  }
  window.location.href = url;
}

export function safeOpen(url) {
  if (!url || typeof url !== 'string') return;
  if (!SAFE_REDIRECT_PREFIXES.some((p) => url.startsWith(p))) {
    console.error('[SECURITY] window.open bloqué vers une URL non whitelistée :', url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export const fmtMoney = (v, currency = "EUR") => {
  const n = v || 0;
  // Affiche les centimes uniquement s'ils existent : "89 €" reste net, "1,50 €" / "130,50 €" exacts.
  const hasCents = Math.round(n * 100) % 100 !== 0;
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(n);
};

// ── Remises (miroir des helpers serveur — le total OFFICIEL reste calculé serveur) ──
export const applyDiscount = (base, type, value) => {
  const v = Number(value) || 0;
  if (type === "percent") return Math.max(0, base * (1 - Math.min(100, Math.max(0, v)) / 100));
  if (type === "amount") return Math.max(0, base - Math.max(0, v));
  return base;
};
export const lineAmount = (it) =>
  applyDiscount((Number(it.quantity) || 0) * (Number(it.unit_price) || 0), it.discount_type, it.discount_value);
export const fmtDiscount = (type, value, currency = "EUR") =>
  type === "percent" ? `−${Number(value) || 0} %` : type === "amount" ? `−${fmtMoney(value, currency)}` : "";

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const defaultResolveItem = (it) => ({ enabled: it?.enabled !== false, quantity: Number(it?.quantity) || 0 });

function itemDepSatisfied(section, item, resolve, seen) {
  if (!item?.requires) return true;
  const key = String(item.id);
  if (seen.has(key)) return true; // cycle : ne pas bloquer (miroir serveur)
  seen.add(key);
  const parent = (section.items || []).find((it) => String(it?.id) === String(item.requires));
  if (!parent) return true;
  const p = resolve(parent, section);
  return p.enabled !== false && (Number(p.quantity) || 0) >= 1 && itemDepSatisfied(section, parent, resolve, seen);
}

// ── Ventilation one-shot / récurrent (miroir de proposals.helpers.ts::sectionTotals/
// proposalTotals — voir ce fichier pour la justification du prorata sur remise de section
// mixte). `resolve(item, section) => {enabled, quantity}` permet de brancher un état
// interactif (offre ajustable côté viewer, choix du prospect) ; par défaut utilise les
// valeurs BRUTES de l'item (`item.enabled`/`item.quantity`), comme le fait le serveur pour
// le total stocké (éditeur, PDF, grilles tarifaires simples sans configuration prospect).
export function sectionAmounts(section, resolve = defaultResolveItem) {
  if (!Array.isArray(section?.items)) return { oneShot: 0, recurring: 0 };
  let oneShotGross = 0;
  let recurringGross = 0;
  for (const it of section.items) {
    const r = resolve(it, section);
    if (r.enabled === false) continue;
    if (!itemDepSatisfied(section, it, resolve, new Set())) continue;
    const amt = applyDiscount((Number(r.quantity) || 0) * (Number(it.unit_price) || 0), it.discount_type, it.discount_value);
    if (it.recurrence === "mensuel") recurringGross += amt;
    else oneShotGross += amt;
  }
  const grossTotal = oneShotGross + recurringGross;
  if (grossTotal <= 0) return { oneShot: 0, recurring: 0 };
  const discounted = applyDiscount(grossTotal, section.discount_type, section.discount_value);
  const oneShot = discounted * (oneShotGross / grossTotal);
  return { oneShot: round2(oneShot), recurring: round2(discounted - oneShot) };
}

export function proposalTotals(sections, resolve = defaultResolveItem) {
  if (!Array.isArray(sections)) return { oneShot: 0, recurring: 0 };
  let oneShot = 0;
  let recurring = 0;
  for (const s of sections) {
    if ((s?.type === "pricing" || s?.type === "configurable") && Array.isArray(s.items)) {
      const t = sectionAmounts(s, resolve);
      oneShot += t.oneShot;
      recurring += t.recurring;
    }
  }
  return { oneShot: round2(oneShot), recurring: round2(recurring) };
}

/**
 * Rend les deux totaux d'une proposition en UNE chaîne compacte, pour les surfaces de
 * synthèse (liste des propositions, activité récente, analytics) où il n'y a de place que
 * pour une valeur. Un abonnement mensuel et des frais uniques ne s'additionnent jamais :
 *   mixte    → « 18 000 € + 500 €/mois »
 *   one-shot → « 18 000 € »
 *   récurrent→ « 500 €/mois »
 *   ni l'un ni l'autre → « 0 € » (formaté dans la devise)
 * `oneShot`/`recurring` viennent de `total_one_shot`/`total_recurring` (repli serveur
 * `resolveDisplayTotals` déjà appliqué) — ne jamais retomber sur le champ `total` mélangé.
 */
export const fmtTotals = (oneShot, recurring, currency = "EUR") => {
  const o = Number(oneShot) || 0;
  const r = Number(recurring) || 0;
  const parts = [];
  if (o > 0) parts.push(fmtMoney(o, currency));
  if (r > 0) parts.push(`${fmtMoney(r, currency)}/mois`);
  return parts.length ? parts.join(" + ") : fmtMoney(0, currency);
};

// Tous les tarifs Acteio sont HT (miroir de computeVat côté serveur — purement
// affichage, aucun calcul officiel ne dépend de cette fonction côté client).
// Renvoie null si le taux n'est pas renseigné : l'appelant affiche alors la
// mention "TVA en sus" plutôt qu'un bloc TTC.
export const computeVat = (ht, vatRate) => {
  const rate = Number(vatRate);
  if (vatRate == null || vatRate === "" || !Number.isFinite(rate) || rate < 0) return null;
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const htR = round2(ht);
  const ttc = round2(htR * (1 + rate / 100));
  const vat = round2(ttc - htR);
  return { ht: htR, vat, ttc };
};

export const STATUS_META = {
  "brouillon": { label: "Brouillon", color: "bg-muted text-muted-foreground border-border" },
  "envoyée": { label: "Envoyée", color: "bg-blue-50 text-blue-700 border-blue-200" },
  "vue": { label: "Vue", color: "bg-amber-50 text-amber-700 border-amber-200" },
  "acceptée": { label: "Acceptée", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  "refusée": { label: "Refusée", color: "bg-rose-50 text-rose-700 border-rose-200" },
};

export default api;
